import { useState, useRef, useEffect, useCallback } from 'react'
import { Volume2, Square, Loader2 } from 'lucide-react'
import { resolveApiBase } from '../lib/mountBase'
import { VOICE_PREVIEW_PHRASE } from '../lib/ttsVoices'

/**
 * Plays a short spoken preview of a Gemini voice via /api/tts. Uses a fixed
 * phrase so the synthesis is cached after the first preview of each voice
 * (shared across users) — previews don't repeatedly spend Gemini quota.
 *
 * Previews synthesize even when the platform engine is "browser" (the backend
 * exempts preview requests) so voices can be auditioned before Gemini is turned
 * on. If synthesis is genuinely unavailable (no key configured, rate-limited),
 * we surface a quiet "unavailable" state rather than erroring.
 */
export function VoicePreviewButton({ voice }: { voice: string }) {
  const [state, setState] = useState<'idle' | 'loading' | 'playing' | 'error'>('idle')
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const urlRef = useRef<string | null>(null)

  const cleanup = useCallback(() => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null }
    if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null }
  }, [])

  // Stop playback if the component unmounts.
  useEffect(() => cleanup, [cleanup])

  const onClick = useCallback(async () => {
    if (state === 'playing' || state === 'loading') {
      cleanup()
      setState('idle')
      return
    }
    setState('loading')
    try {
      const apiBase = resolveApiBase(window.location.pathname)
      const resp = await fetch(`${apiBase}/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // preview:true lets the backend synthesize this fixed phrase even when
        // the platform engine is "browser", so voices are auditionable before
        // Gemini is switched on.
        body: JSON.stringify({ text: VOICE_PREVIEW_PHRASE, voice, preview: true }),
      })
      if (!resp.ok) throw new Error(`tts ${resp.status}`)
      const blob = await resp.blob()
      const url = URL.createObjectURL(blob)
      urlRef.current = url
      const audio = new Audio(url)
      audioRef.current = audio
      audio.onended = () => { cleanup(); setState('idle') }
      audio.onerror = () => { cleanup(); setState('idle') }
      setState('playing')
      await audio.play()
    } catch {
      cleanup()
      setState('error')
      setTimeout(() => setState('idle'), 2500)
    }
  }, [state, voice, cleanup])

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={state === 'playing' ? 'Stop preview' : 'Play voice preview'}
      title={state === 'error' ? 'Preview unavailable' : state === 'playing' ? 'Stop preview' : 'Play a preview'}
      className="inline-flex items-center gap-1.5 px-2.5 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/40 transition-colors disabled:opacity-50"
    >
      {state === 'loading'
        ? <Loader2 className="w-4 h-4 animate-spin" />
        : state === 'playing'
          ? <Square className="w-4 h-4" />
          : <Volume2 className="w-4 h-4" />}
      <span>{state === 'error' ? 'Unavailable' : state === 'playing' ? 'Stop' : 'Preview'}</span>
    </button>
  )
}
